import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import { createProjectKey } from '../../../../utils/projectMatchingUtils';

const useProjectRequest = (ProjectNameRequested) => {
    const { projects } = useSelector((state) => state.projects);
    const { employees } = useSelector((state) => state.employees);

    const [groupedProjects, setGroupedProjects] = useState([]);

    useEffect(() => {
        // Wait for both projects and employees to be loaded before processing
        if (projects?.length > 0 && employees?.length > 0) {

            const filteredProjects = projects.filter((proj) => proj?.isProjecttracking === false)

            // Group by quotationId when present (so same ProjectName+Client+Group but different quotationId = separate rows), else composite key
            const groupedByProjectName = filteredProjects.reduce((acc, project) => {
                const key = project.quotationId ? String(project.quotationId).trim() : createProjectKey(project);
                if (!acc[key]) {
                    acc[key] = [];
                }
                acc[key].push(project);
                return acc;
            }, {});
            // REFACTORED: Extract ProjectName from composite key for display
            // UNDO: Change back to using projectName directly if reverting
            const summarizedData = Object.entries(groupedByProjectName).map(
                ([compositeKey, projectGroup]) => {
                    // Extract ProjectName from first project in group
                    const projectName = projectGroup[0]?.ProjectName || compositeKey.split('||')[0];
                    const allAssignedManagerEmails = projectGroup.flatMap(
                        (p) => p.AssignedManagerEmail || []
                    );

                    // CASE 1: PrimaryResources is "1"
                    const flaggedProject = projectGroup.find(
                        (p) =>
                            p.PrimaryResources === "1" || Number(p.PrimaryResources) === 1
                    );

                    // CASE 2: PrimaryResources is email or array of emails
                    const primaryResourceEmails = flaggedProject
                        ? flaggedProject.AssignedManagerEmail || []
                        : projectGroup.flatMap((p) => {
                            if (
                                typeof p.PrimaryResources === "string" &&
                                p.PrimaryResources.includes("@")
                            ) {
                                return [p.PrimaryResources];
                            } else if (Array.isArray(p.PrimaryResources)) {
                                return p.PrimaryResources.filter((email) =>
                                    email.includes("@")
                                );
                            }
                            return [];
                        });

                    const getNameFromEmail = (email) => {
                        if (!email) return email;
                        // Normalize email for comparison (trim and lowercase)
                        const normalizedEmail = email.trim().toLowerCase();
                        const person = employees.find((emp) => 
                            emp.EmpEmail && emp.EmpEmail.trim().toLowerCase() === normalizedEmail
                        );
                        return person
                            ? `${person.EmpFirstName || ''} ${person.EmpLastName || ''}`.trim()
                            : email;
                    };

                    const AssignedManager = [...new Set(allAssignedManagerEmails)].map(
                        getNameFromEmail
                    );

                    const PrimaryResource = [...new Set(primaryResourceEmails)].map(
                        getNameFromEmail
                    );

                    // ✅ Build detailed ProjectManagerAction array with salespersonstatus
                    const ProjectManagerAction = projectGroup.map((p) => {
                        const managerEmail = (p.AssignedManagerEmail || [])[0]; // Assuming 1 manager per project entry
                        const managerName = getNameFromEmail(managerEmail);

                        // Get salespersonstatus from Assignedperson if available
                        let salesStatus = " ";
                        if (p.Assignedperson && p.Assignedperson.length > 0) {
                            const statuses = p.Assignedperson.map(
                                (person) => person.salespersonstatus
                            );
                            if (statuses.includes("response received")) {
                                salesStatus = "response received";
                            } else if (statuses.includes("project assigned")) {
                                salesStatus = "project assigned";
                            }
                        }

                        const AssignedPerson = p.Assignedperson ? p.Assignedperson.map(person => ({
                            name: person.name,
                            email: person.email,
                            salespersonstatus: person.salespersonstatus || " ",
                            salespersonaction: person.salespersonaction || " ",
                            manageraction: person.manageraction || " ",
                            operationaction: person.operationaction || " ",
                            taskStatus: person.taskStatus || " ",
                            OpDueDate: person.OpDueDate || null,
                            _id: person._id,
                            isProjectoutstanding: person.isProjectoutstanding || false
                        })) : [];

                        return {
                            ProjectId: p._id,
                            Manager: managerName,
                            ManagerEmail: managerEmail,
                            ManagerAction: p.ProjectManagerAction || " ",
                            ResourceType: p.ResourceType || " ",
                            TypeService: p.TypeService,
                            SalesPersonStatus: salesStatus,
                            AssignedPerson: AssignedPerson,
                            ProjectManagerActionSummary: p.ProjectManagerAction

                        };
                    });

                    // const ProjectManagerActionSummary =
                    //   projectGroup.find((p) => p.ProjectManagerAction)?.ProjectManagerAction ||
                    //   "";
                    return {
                        ProjectName: projectName,
                        ClientName: projectGroup[0]?.ClientName || '',
                        AssignedGroup: projectGroup[0]?.AssignedGroup || '',
                        quotationId: projectGroup[0]?.quotationId || null,
                        DateDue: projectGroup[0].ProjectDueDate,
                        AssignedManager,
                        PrimaryResource,
                        ProjectManagerAction,
                    };
                }
            );
            setGroupedProjects(summarizedData);
        }
    }, [projects, employees]);

    const [matchedForwardedProject, setMatchedForwardedProject] = useState([])


    // Match by quotationId when available, else by ProjectName (for backward compat)
    useEffect(() => {
        if (ProjectNameRequested && groupedProjects?.length > 0) {
            const match = groupedProjects.filter((pro) =>
                pro?.quotationId ? pro.quotationId === ProjectNameRequested : pro?.ProjectName === ProjectNameRequested
            );
            if (match.length) setMatchedForwardedProject(match);
            else {
                const byName = groupedProjects.filter((pro) => pro?.ProjectName === ProjectNameRequested);
                setMatchedForwardedProject(byName);
            }
        }
    }, [ProjectNameRequested, groupedProjects])

    return (
        {
            matchedForwardedProject,
            groupedProjects,
        }
    )
}

export default useProjectRequest
